/*
 * *****************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Tue, Jul 20th 2021, 11:02:32 am
 *
 * *****************************************************
 */

import { useCallback, useEffect, useState } from 'react';

import { TextStyle, Link, Button, Form, FormLayout } from '@shopify/polaris';
import { Form as FinalForm, Field } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { Redirect, RouteComponentProps, useHistory } from 'react-router-dom';

import { INPUT_MAX_LENGTH } from 'constants/form';
import { PRIVACY_POLICY_LINK, TERMS_OF_USE_LINK } from 'constants/links';
import {
  SIGN_UP_WITH_EMAIL_PASSWORD_RECAPTCHA_ACTION,
  SIGN_UP_WITH_GOOGLE_RECAPTCHA_ACTION
} from 'constants/recaptcha-actions';
import { SIGN_IN, VERIFY_EMAIL, CREATE_WORKSPACE } from 'features/auth/routes/paths';
import PageErrorBanner from 'features/common/views/components/PageErrorBanner';
import { authAPI } from 'states/services/auth';
import {
  useSignUpWithEmailPasswordMutation,
  useSignUpWithGoogleMutation
} from 'states/services/auth/hooks';
import {
  AuthenticationEmailPasswordRequest,
  AuthenticationGoogleRequest
} from 'states/services/auth/types';
import {
  ErrorObject,
  SubmissionResponse,
  convertApiErrorsToFinalFormSubmitErrors
} from 'utils/form/convert';
import { composeValidators, required, mustBeEmail, maxLength } from 'utils/form/validation';
import { useAppDispatch, useGoogleReCaptchaV3 } from 'utils/hooks';

import Divider from '../../components/Divider';
import GoogleButton from '../../components/GoogleButton';
import TextFieldAdapter from '../../components/TextFieldAdapter';
import AuthLayout from '../../layouts/AuthLayout';
import { ContentContainer } from './styles';

const SignUp = ({ location }: RouteComponentProps) => {
  const history = useHistory();
  /**
   * pageError is our single souce of true for page error
   */
  const [pageError, setPageError] = useState<ErrorObject | undefined>(undefined);
  /**
   * [apiTrigger, apiResult] of RTK Mutation
   */
  const [signUpWithEmailPassword, signUpWithEmailPasswordStatus] =
    useSignUpWithEmailPasswordMutation();
  const [signUpWithGoogle, signUpWithGoogleStatus] = useSignUpWithGoogleMutation();

  const { isReady: isReCaptchaReady, execute: executeReCaptcha } = useGoogleReCaptchaV3();
  const dispatch = useAppDispatch();

  /**
   * Reset API and page errors
   */
  const handleResetError = useCallback(() => {
    setPageError(undefined);
    dispatch(authAPI.util.resetApiState());
  }, [dispatch]);

  /**
   * Sign up using email and password
   */
  const handleFormSubmit = useCallback(
    async values => {
      handleResetError();
      /**
       * Get the captcha token
       */
      if (!isReCaptchaReady) {
        const errorObject = {
          data: {
            message: 'Execute recaptcha not yet available. Please try again!'
          }
        };

        /**
         * Display captcha error
         */
        setPageError(errorObject);
        return undefined; // undefined means no submission errors
      }

      const captchaToken = await executeReCaptcha(SIGN_UP_WITH_EMAIL_PASSWORD_RECAPTCHA_ACTION);

      /**
       * Get email value
       */
      const { email } = values;

      /**
       * Prepare the request data
       */
      const data: AuthenticationEmailPasswordRequest = {
        strategy: 'email_password',
        email,
        captchaToken
      };

      /**
       * Make the request
       */
      const res = (await signUpWithEmailPassword(data)) as SubmissionResponse;

      if (res.error) {
        /**
         * Return the submission errors for displaying (if any)
         */
        return convertApiErrorsToFinalFormSubmitErrors(res.error);
      }

      history.push({
        pathname: VERIFY_EMAIL,
        state: { referrer: location.pathname, email }
      });

      return undefined;
    },
    [
      handleResetError,
      isReCaptchaReady,
      executeReCaptcha,
      signUpWithEmailPassword,
      history,
      location
    ]
  );

  /**
   * Sign up using Google
   */
  const handleGoogleSignInSuccess = useCallback(
    async res => {
      handleResetError();

      /**
       * Get the captcha token
       */
      if (!isReCaptchaReady) {
        const errorObject = {
          data: {
            message: 'Execute recaptcha not yet available. Please try again!'
          }
        };

        /**
         * Display captcha error
         */
        setPageError(errorObject);
        return;
      }
      const captchaToken = await executeReCaptcha(SIGN_UP_WITH_GOOGLE_RECAPTCHA_ACTION);

      /**
       * Prepare request data and make the request
       */
      const { tokenId: token } = res;
      const data: AuthenticationGoogleRequest = {
        strategy: 'google_login',
        token,
        captchaToken
      };

      await signUpWithGoogle(data);
    },
    [handleResetError, isReCaptchaReady, executeReCaptcha, signUpWithGoogle]
  );

  const handleGoogleSignInFailure = useCallback(error => {
    if (error && error.error === 'popup_closed_by_user') return;

    const errorObject = {
      data: {
        message: 'Error connecting Google. Please try again!'
      }
    };
    setPageError(errorObject);
  }, []);

  useEffect(() => {
    /**
     * pageError syncs with API error
     */
    const error = signUpWithGoogleStatus.error ?? signUpWithEmailPasswordStatus.error;
    setPageError(error as ErrorObject);
  }, [signUpWithGoogleStatus.error, signUpWithEmailPasswordStatus.error]);

  if (signUpWithGoogleStatus.isSuccess)
    return (
      <Redirect
        to={{
          pathname: CREATE_WORKSPACE,
          state: { referrer: location.pathname }
        }}
      />
    );

  return (
    <AuthLayout>
      <Helmet>
        <title>Get started - Register account | WiseIO</title>
        <meta name="description" content="Register an account for your workspace at WiseIO" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>
      <header>
        <h1>Register an account</h1>
        <span>
          <TextStyle variation="subdued">Already have an account?</TextStyle>&nbsp;
          <Link removeUnderline url={SIGN_IN}>
            Sign in here
          </Link>
          .
        </span>
      </header>
      <ContentContainer>
        {pageError && <PageErrorBanner data={pageError} />}
        <GoogleButton
          loading={signUpWithGoogleStatus.isLoading}
          onSuccess={handleGoogleSignInSuccess}
          onFailure={handleGoogleSignInFailure}
        />
        <Divider />
        <FinalForm
          onSubmit={handleFormSubmit}
          render={({ handleSubmit, submitting }) => (
            <Form onSubmit={handleSubmit}>
              <FormLayout>
                <Field
                  name="email"
                  component={TextFieldAdapter}
                  label="Email"
                  disabled={submitting}
                  type="email"
                  placeholder="Enter your email to sign up"
                  labelHidden
                  validate={composeValidators(
                    required('Email'),
                    mustBeEmail,
                    maxLength('Email', INPUT_MAX_LENGTH)
                  )}
                />
                <Button submit primary disabled={submitting} loading={submitting}>
                  Continue with email
                </Button>
              </FormLayout>
            </Form>
          )}
        />
      </ContentContainer>
      <footer>
        <TextStyle variation="subdued">
          By signing up to WiseIO, you agree to our
          <br />
          <Link removeUnderline url={TERMS_OF_USE_LINK}>
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link removeUnderline url={PRIVACY_POLICY_LINK}>
            Privacy Policy
          </Link>
          .
        </TextStyle>
      </footer>
    </AuthLayout>
  );
};

export default SignUp;
